/* ────────────────────────────────────────────────────────────────────────────
 * MOCK GENERATION PROVIDER  —  TEMPORARY, REPLACE WITH MAESTRO
 * ────────────────────────────────────────────────────────────────────────────
 * Simulates a full generation run: a short queue wait, a sampling phase with
 * per-step progress + ETA, then a finalizing phase. It never fails, and it
 * returns a real, playable, downloadable clip produced by `renderClip`.
 *
 * TO GO LIVE: see `src/generation/index.ts` — flip ACTIVE_PROVIDER to the
 * Maestro provider. Nothing else in the app needs to change.
 * ──────────────────────────────────────────────────────────────────────────── */

import { renderClip } from './renderClip';
import type {
  GenerationProvider,
  GenerationResult,
  StartJobOptions,
} from './types';

/** Wall-clock length of the produced clip. */
const CLIP_SECONDS = 3;

/** How long the fake queue wait lasts. */
const QUEUE_MS = 900;
/** How long the fake "muxing / writing file" tail lasts. */
const FINALIZE_MS = 700;

const RESOLUTION_WEIGHT: Record<string, number> = {
  '480p': 0.75,
  '720p': 1,
  '1080p': 1.5,
};

const abortError = () => new DOMException('Generation cancelled.', 'AbortError');

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(abortError());
    const id = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(id);
      reject(abortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** Estimated total run time for a request, in seconds. */
function estimateSeconds(steps: number, resolution: string): number {
  const weight = RESOLUTION_WEIGHT[resolution] ?? 1;
  const sampling = Math.max(CLIP_SECONDS, (steps / 30) * 5 * weight);
  return (QUEUE_MS + FINALIZE_MS) / 1000 + sampling;
}

export const mockProvider: GenerationProvider = {
  id: 'mock',
  label: 'Mock engine',
  isLive: false,

  estimateSeconds(settings) {
    return estimateSeconds(settings.steps, settings.resolution);
  },

  async healthCheck() {
    // The mock is always "up" — but it reports isLive:false so the UI badge
    // stays honest about there being no real engine behind it.
    return true;
  },

  async generate({ request, onProgress, signal }: StartJobOptions): Promise<GenerationResult> {
    if (signal.aborted) throw abortError();

    const { steps, resolution } = request.settings;
    const totalSeconds = estimateSeconds(steps, resolution);
    const samplingSeconds = Math.max(
      CLIP_SECONDS,
      totalSeconds - (QUEUE_MS + FINALIZE_MS) / 1000,
    );

    // ── 1. Queued ───────────────────────────────────────────────────────────
    const queueStart = performance.now();
    onProgress({
      stage: 'queued',
      progress: 0,
      etaSeconds: totalSeconds,
      message: 'Queued — reserving the engine',
    });
    // Tick the queue phase so the bar moves immediately.
    while (performance.now() - queueStart < QUEUE_MS) {
      await sleep(100, signal);
      const p = Math.min(1, (performance.now() - queueStart) / QUEUE_MS);
      onProgress({
        stage: 'queued',
        progress: p * 0.05,
        etaSeconds: totalSeconds - (p * QUEUE_MS) / 1000,
        message: 'Queued — reserving the engine',
      });
    }

    // ── 2. Generating (real-time canvas render + recording) ─────────────────
    // The recorder runs for CLIP_SECONDS; the sampling phase is stretched to
    // `samplingSeconds`, so progress stays in sync with the wall clock.
    const samplingStart = performance.now();
    let clipDone = false;

    const reportSampling = () => {
      const elapsed = (performance.now() - samplingStart) / 1000;
      const p = Math.min(1, elapsed / samplingSeconds);
      const step = Math.min(steps, Math.max(1, Math.round(p * steps)));
      onProgress({
        stage: 'generating',
        progress: 0.05 + p * 0.85,
        etaSeconds: Math.max(0, samplingSeconds - elapsed + FINALIZE_MS / 1000),
        message: `Sampling — step ${step}/${steps}`,
        step,
        totalSteps: steps,
      });
    };

    const ticker = window.setInterval(reportSampling, 90);
    let result: GenerationResult;
    try {
      result = await renderClip({
        request,
        durationSeconds: CLIP_SECONDS,
        signal,
        onTick: () => {
          if (!clipDone) reportSampling();
        },
      });
      clipDone = true;

      // Recording finished but the simulated sampling phase may be longer —
      // keep the UI honest by waiting out the remainder.
      const remaining = samplingSeconds * 1000 - (performance.now() - samplingStart);
      if (remaining > 0) await sleep(remaining, signal);
      reportSampling();
    } finally {
      window.clearInterval(ticker);
    }

    // ── 3. Finalizing ───────────────────────────────────────────────────────
    onProgress({
      stage: 'finalizing',
      progress: 0.92,
      etaSeconds: FINALIZE_MS / 1000,
      message: 'Encoding and writing file',
    });
    await sleep(FINALIZE_MS, signal);

    onProgress({
      stage: 'done',
      progress: 1,
      etaSeconds: 0,
      message: 'Done',
    });

    return result;
  },

  releaseResult(result) {
    if (result.videoUrl.startsWith('blob:')) URL.revokeObjectURL(result.videoUrl);
  },
};
